import { Injectable, inject } from '@angular/core';

import { DatabaseService } from '../database.service';
import {
  ConflictRecord,
  EntityName,
  SyncOperation,
} from '../models/sync.model';

/**
 * Backoff schedule for a failed operation.
 *
 * Exponential from one second, doubling, capped at five minutes so a long
 * outage does not push the next attempt hours away. The jitter matters: a
 * classroom of tablets loses wifi together and regains it together, and without
 * it every device would retry on the same tick and hammer the API in lockstep.
 */
export function backoffDelayMs(attempts: number): number {
  const base = 1_000;
  const cap = 5 * 60_000;
  const exponential = Math.min(cap, base * 2 ** Math.max(0, attempts - 1));
  const jitter = exponential * 0.25 * Math.random();
  return Math.round(exponential + jitter);
}

/**
 * After this many failures an operation stops being retried automatically and
 * is surfaced for a human. It is never discarded: silently dropping a teacher's
 * saved answer is the one outcome this whole subsystem exists to prevent.
 */
export const MAX_AUTOMATIC_ATTEMPTS = 8;

/** Reads and writes over the sync_queue and conflicts tables. */
@Injectable({ providedIn: 'root' })
export class SyncQueueRepository {
  private readonly databaseService = inject(DatabaseService);

  private async db() {
    return this.databaseService.ready();
  }

  /**
   * Operations that are due now, oldest first.
   *
   * Ordering by creation is what preserves causality: an attempt's create must
   * reach the server before the answers that reference it.
   */
  async takeDue(limit: number, now = Date.now()): Promise<SyncOperation[]> {
    const db = await this.db();
    const due = await db.syncQueue
      .where('status')
      .anyOf('pending', 'failed')
      .and((op) => op.nextAttemptAt <= now && op.attempts < MAX_AUTOMATIC_ATTEMPTS)
      .sortBy('createdAt');
    return due.slice(0, limit);
  }

  /** Mark a batch in flight so a second sync pass cannot pick it up too. */
  async markSyncing(ops: SyncOperation[]): Promise<void> {
    if (!ops.length) return;
    const db = await this.db();
    const now = Date.now();
    await db.transaction('rw', db.syncQueue, async () => {
      for (const op of ops) {
        await db.syncQueue.update(op.id as number, {
          status: 'syncing',
          updatedAt: now,
        });
      }
    });
  }

  /** The operation succeeded and can leave the queue. */
  async remove(opId: number): Promise<void> {
    const db = await this.db();
    await db.syncQueue.delete(opId);
  }

  /** Schedule a retry with backoff. */
  async markFailed(op: SyncOperation, error: string): Promise<void> {
    const db = await this.db();
    const attempts = op.attempts + 1;
    await db.syncQueue.update(op.id as number, {
      status: 'failed',
      attempts,
      nextAttemptAt: Date.now() + backoffDelayMs(attempts),
      lastError: error.slice(0, 500),
      updatedAt: Date.now(),
    });
  }

  /**
   * Return an operation to the queue untouched.
   *
   * Used when a batch is abandoned for a reason that is not the operation's
   * fault - the connection dropped mid-flight, say. Incrementing `attempts`
   * there would push a perfectly good operation towards the failure ceiling
   * because of an outage it had nothing to do with.
   */
  async release(op: SyncOperation): Promise<void> {
    const db = await this.db();
    await db.syncQueue.update(op.id as number, {
      status: 'pending',
      updatedAt: Date.now(),
    });
  }

  /** Clear the queue for a record whose conflict has been recorded. */
  async removeForRecord(entity: EntityName, localId: number): Promise<void> {
    const db = await this.db();
    await db.syncQueue
      .where('[entity+localId]')
      .equals([entity, localId])
      .delete();
  }

  async countPending(): Promise<number> {
    const db = await this.db();
    return db.syncQueue.where('status').anyOf('pending', 'syncing').count();
  }

  async countFailed(): Promise<number> {
    const db = await this.db();
    return db.syncQueue.where('status').equals('failed').count();
  }

  /** Operations that exhausted automatic retries and need a human. */
  async listExhausted(): Promise<SyncOperation[]> {
    const db = await this.db();
    return db.syncQueue
      .where('status')
      .equals('failed')
      .and((op) => op.attempts >= MAX_AUTOMATIC_ATTEMPTS)
      .toArray();
  }

  /** Put an exhausted operation back in play, e.g. from a "Retry" button. */
  async resetAttempts(opId: number): Promise<void> {
    const db = await this.db();
    await db.syncQueue.update(opId, {
      status: 'pending',
      attempts: 0,
      nextAttemptAt: Date.now(),
      lastError: null,
      updatedAt: Date.now(),
    });
  }

  /**
   * Recover operations abandoned mid-flight.
   *
   * A tab closed while a batch was in flight leaves rows stuck in `syncing`
   * forever, because the code that would have finished them is gone. Called on
   * startup, this returns them to `pending`. Safe because every operation is
   * idempotent server-side via `opId`: if the batch did land, replaying it
   * returns the original result instead of applying anything twice.
   */
  async recoverStuck(): Promise<number> {
    const db = await this.db();
    const stuck = await db.syncQueue.where('status').equals('syncing').toArray();
    if (!stuck.length) return 0;
    await db.transaction('rw', db.syncQueue, async () => {
      for (const op of stuck) {
        await db.syncQueue.update(op.id as number, {
          status: 'pending',
          nextAttemptAt: Date.now(),
          updatedAt: Date.now(),
        });
      }
    });
    return stuck.length;
  }

  // ── Conflicts ────────────────────────────────────────────────────────────

  async recordConflict(conflict: Omit<ConflictRecord, 'id'>): Promise<number> {
    const db = await this.db();
    return db.conflicts.add(conflict as ConflictRecord);
  }

  async listOpenConflicts(): Promise<ConflictRecord[]> {
    const db = await this.db();
    return db.conflicts.filter((c) => !c.resolvedAt).toArray();
  }

  async countOpenConflicts(): Promise<number> {
    const db = await this.db();
    return db.conflicts.filter((c) => !c.resolvedAt).count();
  }

  async getConflictFor(
    entity: EntityName,
    localId: number,
  ): Promise<ConflictRecord | undefined> {
    const db = await this.db();
    return db.conflicts
      .where('[entity+localId]')
      .equals([entity, localId])
      .filter((c) => !c.resolvedAt)
      .first();
  }

  async closeConflict(
    conflictId: number,
    resolution: ConflictRecord['resolution'],
  ): Promise<void> {
    const db = await this.db();
    await db.conflicts.update(conflictId, {
      resolvedAt: Date.now(),
      resolution,
    });
  }

  // ── Cursors ──────────────────────────────────────────────────────────────

  /** Where the last pull for this entity stopped. */
  async getCursor(entity: EntityName): Promise<string | number | null> {
    const db = await this.db();
    const row = await db.syncMeta.get(`cursor:${entity}`);
    return row?.value ?? null;
  }

  async setCursor(entity: EntityName, value: string | number | null): Promise<void> {
    const db = await this.db();
    await db.syncMeta.put({
      key: `cursor:${entity}`,
      value,
      updatedAt: Date.now(),
    });
  }

  /**
   * Which user this local database belongs to.
   *
   * Stored so a change of teacher on a shared device is detectable - see
   * SyncService.ensureUserScope.
   */
  async getUserKey(): Promise<string | null> {
    const db = await this.db();
    const row = await db.syncMeta.get('userKey');
    return typeof row?.value === 'string' ? row.value : null;
  }

  async setUserKey(key: string): Promise<void> {
    const db = await this.db();
    await db.syncMeta.put({ key: 'userKey', value: key, updatedAt: Date.now() });
  }

  async getLastSyncedAt(): Promise<number | null> {
    const db = await this.db();
    const row = await db.syncMeta.get('lastSyncedAt');
    return typeof row?.value === 'number' ? row.value : null;
  }

  async setLastSyncedAt(at: number): Promise<void> {
    const db = await this.db();
    await db.syncMeta.put({ key: 'lastSyncedAt', value: at, updatedAt: at });
  }
}
